/**
 * Năm trạng thái UI bắt buộc — docs/08 §8.5:
 * đang tải (skeleton, không spinner toàn trang) · có dữ liệu · rỗng ·
 * lỗi (có nút thử lại) · không đủ quyền.
 */

import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';
import { ApiError } from '../lib/api';

export function Skeleton({ height = 16, width }: { height?: number; width?: string | number }) {
  return (
    <span
      className="skeleton"
      style={{
        display: 'block',
        height,
        width: width ?? '100%',
        borderRadius: 'var(--r-sm)',
        background:
          'linear-gradient(90deg, var(--surface-2) 25%, color-mix(in oklch, var(--surface-2) 60%, var(--border)) 50%, var(--surface-2) 75%)',
        backgroundSize: '200% 100%',
        animation: 'dnshub-shimmer 1.3s linear infinite',
      }}
      aria-hidden="true"
    />
  );
}

export function SkeletonCard({ rows = 4 }: { rows?: number }) {
  return (
    <div className="card">
      <div className="card-body stack" aria-busy="true">
        <Skeleton height={20} width="42%" />
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} height={14} width={i % 2 === 0 ? '100%' : '76%'} />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon = 'info',
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: string;
}) {
  return (
    <div className="empty">
      <span style={{ display: 'inline-flex', color: 'var(--muted)', marginBottom: 8 }}>
        <Icon name={icon} size={26} />
      </span>
      <h3>{title}</h3>
      {body ? <p>{body}</p> : null}
      {action ? (
        <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
          {action}
        </div>
      ) : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const message =
    error instanceof ApiError ? error.message : error instanceof Error ? error.message : undefined;

  return (
    <EmptyState
      icon="warn"
      title={t('state.error.title')}
      body={message ?? t('state.error.body')}
      action={
        onRetry ? (
          <button className="btn" type="button" onClick={onRetry}>
            <Icon name="refresh" size={16} />
            {t('common.retry')}
          </button>
        ) : undefined
      }
    />
  );
}

export function ForbiddenState({ body }: { body?: string }) {
  const t = useT();
  return (
    <EmptyState
      icon="shield"
      title={t('state.forbidden.title')}
      body={body ?? t('state.forbidden.body')}
    />
  );
}

/**
 * Bọc một vùng dữ liệu: tự chọn đúng một trong năm trạng thái.
 */
export function AsyncSection<T>({
  loading,
  error,
  data,
  onRetry,
  empty,
  children,
  skeleton,
}: {
  loading: boolean;
  error: unknown;
  data: T | undefined;
  onRetry?: () => void;
  empty?: ReactNode;
  skeleton?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (loading) return <>{skeleton ?? <SkeletonCard />}</>;
  if (error) {
    if (error instanceof ApiError && error.status === 403)
      return <ForbiddenState body={error.message} />;
    return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  }
  if (data === undefined || data === null) return <>{empty ?? <EmptyState title="—" />}</>;
  if (Array.isArray(data) && data.length === 0 && empty) return <>{empty}</>;
  return <>{children(data)}</>;
}
